import React, { useEffect, useState } from "react";
import { Link, Navigate, useNavigate } from "react-router-dom";
import { ArrowRight, Moon, Sun } from "lucide-react";
import { useAuth } from "../lib/auth";
import { useSettings } from "../lib/settings";
import { useTheme } from "../lib/theme";
import Logo from "../components/Logo";
import { LOGIN_BACKGROUND } from "../lib/constants";

export default function Login() {
  const { user, login } = useAuth();
  const { settings } = useSettings();
  const { theme, toggle } = useTheme();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);
  const navigate = useNavigate();

  // If user hasn't explicitly chosen a theme, follow the server-configured default.
  // toggle is stable (useCallback in ThemeProvider) so it's safe in deps.
  useEffect(() => {
    try {
      const stored = window.localStorage.getItem("atech-theme");
      if (!stored && settings?.default_theme && settings.default_theme !== theme) {
        if (settings.default_theme === "light" && theme === "dark") toggle();
        if (settings.default_theme === "dark" && theme === "light") toggle();
      }
    } catch (_e) { /* private-mode guard */ }
  }, [settings?.default_theme, theme, toggle]);

  if (user) {
    return <Navigate to={user.role === "admin" ? "/admin" : "/portal"} replace />;
  }

  const onSubmit = async (e) => {
    e.preventDefault();
    setErr(""); setBusy(true);
    try {
      const u = await login(email.trim(), password);
      navigate(u.role === "admin" ? "/admin" : "/portal");
    } catch (e2) { setErr(e2.message); }
    finally { setBusy(false); }
  };

  // Preview-only convenience: quick sign-in with seeded demo accounts.
  const isPreview = typeof window !== "undefined" && window.location.hostname.includes("preview.emergentagent.com");
  const quickLogin = async (demoEmail, demoPassword) => {
    setErr(""); setBusy(true);
    try {
      const u = await login(demoEmail, demoPassword);
      navigate(u.role === "admin" ? "/admin" : "/portal");
    } catch (e2) { setErr(e2.message); }
    finally { setBusy(false); }
  };

  const isLight = theme === "light";

  return (
    <div className="min-h-screen grid grid-cols-1 lg:grid-cols-[1.1fr_1fr] bg-[color:var(--atech-bg)] transition-colors duration-500">
      {/* Content side */}
      <div className="relative flex flex-col px-8 sm:px-16 py-10 lg:py-14">
        <div className="flex items-center justify-between">
          <Logo size={40} />
          <button
            onClick={toggle}
            aria-label="Toggle theme"
            className="flex items-center gap-2 text-xs px-3 py-2 rounded-[2px] border border-[color:var(--atech-border)] text-[color:var(--atech-text-dim)] hover:text-[color:var(--atech-text)] hover:border-[color:var(--atech-border-strong)] transition-colors"
            data-testid="theme-toggle"
          >
            {isLight ? <Moon size={14} /> : <Sun size={14} />}
            <span className="tracking-[0.16em] uppercase">{isLight ? "Dark" : "Light"}</span>
          </button>
        </div>

        <div className="flex-1 flex flex-col justify-center max-w-xl mt-10 lg:mt-0 fade-up">
          {settings?.login_eyebrow && (
            <div className="label text-[color:var(--atech-copper)] mb-5" data-testid="login-eyebrow">
              {settings.login_eyebrow}
            </div>
          )}
          <h1
            className="font-display text-[2.4rem] sm:text-[3rem] lg:text-[3.6rem] leading-[1.05] tracking-tight text-[color:var(--atech-text)]"
            data-testid="login-headline"
          >
            {settings?.login_headline}
          </h1>
          <p
            className="mt-7 text-[15px] text-[color:var(--atech-text-dim)] leading-[1.75] max-w-lg whitespace-pre-line"
            data-testid="login-body"
          >
            {settings?.login_body}
          </p>

          <form onSubmit={onSubmit} className="mt-12 space-y-5 max-w-md" data-testid="login-form">
            <div>
              <label htmlFor="email" className="label block mb-2">Email</label>
              <input
                id="email"
                type="email"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="you@company.co.uk"
                className="w-full bg-[color:var(--atech-surface)] border border-[color:var(--atech-border)] focus:border-[color:var(--atech-copper)] focus:outline-none focus:ring-1 focus:ring-[color:var(--atech-copper)] rounded-[2px] px-4 py-3 text-[color:var(--atech-text)] placeholder:text-[color:var(--atech-text-muted)]"
                data-testid="login-email-input"
              />
            </div>
            <div>
              <div className="flex items-center justify-between mb-2">
                <label htmlFor="password" className="label">Password</label>
                <Link
                  to="/forgot-password"
                  className="text-[10px] tracking-[0.12em] uppercase text-[color:var(--atech-text-muted)] hover:text-[color:var(--atech-copper)] transition-colors"
                  data-testid="forgot-password-link"
                >
                  Forgot password?
                </Link>
              </div>
              <input
                id="password"
                type="password"
                required
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="••••••••"
                className="w-full bg-[color:var(--atech-surface)] border border-[color:var(--atech-border)] focus:border-[color:var(--atech-copper)] focus:outline-none focus:ring-1 focus:ring-[color:var(--atech-copper)] rounded-[2px] px-4 py-3 text-[color:var(--atech-text)] placeholder:text-[color:var(--atech-text-muted)]"
                data-testid="login-password-input"
              />
            </div>
            {err && (
              <div className="text-xs text-[#F87171] border border-[rgba(220,38,38,0.25)] bg-[rgba(220,38,38,0.08)] rounded-[2px] px-3 py-2" data-testid="login-error">
                {err}
              </div>
            )}
            <button
              type="submit"
              disabled={busy}
              className="btn-copper w-full flex items-center justify-center gap-2 disabled:opacity-60"
              data-testid="login-submit-button"
            >
              {busy ? "Signing in…" : "Sign in"}
              <ArrowRight size={16} />
            </button>
          </form>

          {isPreview && (
            <div className="mt-6 max-w-md rounded-[2px] border border-dashed border-[color:var(--atech-copper)]/40 bg-[rgba(198,132,88,0.05)] p-4" data-testid="preview-quick-login">
              <div className="text-[9px] uppercase tracking-[0.16em] text-[color:var(--atech-copper)] mb-2.5">Preview only · quick sign-in</div>
              <div className="flex flex-wrap gap-2">
                <button
                  type="button"
                  disabled={busy}
                  onClick={() => quickLogin("admin@atech-design.co.uk", "AtechAdmin2026!")}
                  className="flex-1 min-w-[140px] text-xs px-3 py-2.5 rounded-[2px] border border-[color:var(--atech-border)] text-[color:var(--atech-text-dim)] hover:text-[color:var(--atech-text)] hover:border-[color:var(--atech-copper)] transition-colors disabled:opacity-50"
                  data-testid="quick-login-admin"
                >
                  Sign in as Admin
                </button>
                <button
                  type="button"
                  disabled={busy}
                  onClick={() => quickLogin("demo.client@atech-design.co.uk", "DemoClient2026!")}
                  className="flex-1 min-w-[140px] text-xs px-3 py-2.5 rounded-[2px] border border-[color:var(--atech-border)] text-[color:var(--atech-text-dim)] hover:text-[color:var(--atech-text)] hover:border-[color:var(--atech-copper)] transition-colors disabled:opacity-50"
                  data-testid="quick-login-client"
                >
                  Sign in as Demo client
                </button>
              </div>
              <div className="text-[10px] text-[color:var(--atech-text-muted)] mt-2.5">This shortcut is hidden on the live site.</div>
            </div>
          )}

          {settings?.login_footer && (
            <div className="mt-10 text-[11px] text-[color:var(--atech-text-muted)] leading-relaxed max-w-md" data-testid="login-footer">
              {settings.login_footer}
            </div>
          )}

          {settings?.login_gdpr && (
            <div className="mt-6 border-t border-[color:var(--atech-border)] pt-5 text-[11px] text-[color:var(--atech-text-muted)] leading-relaxed max-w-md" data-testid="login-gdpr">
              <span className="uppercase tracking-[0.14em] text-[9px] block mb-1.5 text-[color:var(--atech-copper)] opacity-70">Security &amp; Privacy</span>
              {settings.login_gdpr}
            </div>
          )}
        </div>

        <div className="mt-12 label text-[color:var(--atech-text-muted)]">
          {settings?.practice_name || "A-Tech Architectural Design"} · 2026
        </div>
      </div>

      {/* Visual side */}
      <div
        className="relative hidden lg:block atech-grain overflow-hidden"
        style={{
          backgroundImage: isLight
            ? `linear-gradient(140deg, rgba(237,240,245,0.92) 0%, rgba(180,95,44,0.35) 55%, rgba(22,34,49,0.55) 100%), url('${LOGIN_BACKGROUND}')`
            : `linear-gradient(140deg, rgba(12,12,14,0.92) 0%, rgba(22,34,49,0.55) 60%, rgba(12,12,14,0.1) 100%), url('${LOGIN_BACKGROUND}')`,
          backgroundSize: "cover",
          backgroundPosition: "center",
        }}
      >
        <div className="absolute inset-x-12 bottom-14 z-10 max-w-md">
          <div className="label text-[color:var(--atech-copper)] mb-3">The portal</div>
          <div className="font-display text-3xl leading-tight tracking-tight text-white keep-white drop-shadow">
            {settings?.login_side_caption}
          </div>
        </div>
      </div>
    </div>
  );
}
